import React, { useState, useMemo } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { sendRequestEmail } from '../../services/email';

const initialState = {
  type: 'blog',
  name: '',
  description: '',
  email: '',
  phone: '',
};

const RequestModal = ({ show, onHide }) => {
  const [form, setForm] = useState(initialState);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const recipient = useMemo(() => process.env.REACT_APP_REQUEST_RECIPIENT || '', []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const reset = () => {
    setForm(initialState);
    setError('');
    setSuccess('');
    setSubmitting(false);
  };

  const handleClose = () => {
    if (!submitting) {
      reset();
      onHide && onHide();
    }
  };

  const validate = () => {
    if (!form.type) return 'Please select a request type.';
    if (!form.name.trim()) return 'Please enter your name.';
    if (!form.description.trim()) return 'Please add a short description.';
    if (!form.email.trim() && !form.phone.trim()) return 'Provide at least an email or a phone number.';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Please enter a valid email address.';
    if (!recipient) return 'Recipient email is not configured. Set REACT_APP_REQUEST_RECIPIENT in .env.';
    return '';
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setSubmitting(true);
    try {
      await sendRequestEmail({
        request_type: form.type,
        user_name: form.name,
        description: form.description,
        contact_email: form.email,
        contact_phone: form.phone,
        recipient,
      });
      setSuccess('Your request has been sent. Thank you!');
      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (err) {
      setError(err?.message || 'Failed to send your request. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Request Video / Blog / Prompt</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
          {success && <Alert variant="success" className="mb-3">{success}</Alert>}

          <Form.Group className="mb-3" controlId="reqType">
            <Form.Label>Type</Form.Label>
            <Form.Select name="type" value={form.type} onChange={onChange} required>
              <option value="blog">Blog</option>
              <option value="video">Video</option>
              <option value="prompt">Prompt</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="reqName">
            <Form.Label>Your Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter your name"
              name="name"
              value={form.name}
              onChange={onChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="reqDesc">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Describe the topic or details"
              name="description"
              value={form.description}
              onChange={onChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="reqEmail">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              placeholder="your@email.com (optional)"
              name="email"
              value={form.email}
              onChange={onChange}
            />
            <Form.Text muted>Provide email or phone (at least one).</Form.Text>
          </Form.Group>

          <Form.Group className="mb-2" controlId="reqPhone">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              type="tel"
              placeholder="+91 98765 43210 (optional)"
              name="phone"
              value={form.phone}
              onChange={onChange}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" /> Sending
              </>
            ) : (
              'Send'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default RequestModal;
